"use client"

import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import API from "../utils/api"
import "./ProductDisplay.css"
import { format } from "date-fns"

const ProductDisplay = () => {
  const [allProducts, setAllProducts] = useState([])
  const [productQuery, setProductQuery] = useState("")
  const [productResults, setProductResults] = useState([])
  const [isSearchingProducts, setIsSearchingProducts] = useState(false)

  const [dateQuery, setDateQuery] = useState("")
  const [dateResults, setDateResults] = useState([])
  const [isSearchingDate, setIsSearchingDate] = useState(false)

  const [selectedProductId, setSelectedProductId] = useState("")
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [isLoadingProduct, setIsLoadingProduct] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  const navigate = useNavigate()

  useEffect(() => {
    fetchAllProducts()
  }, [])

  const fetchAllProducts = async () => {
    try {
      const res = await API.get("/products")
      setAllProducts(Array.isArray(res.data) ? res.data : [])
    } catch (err) {
      setAllProducts([])
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      handleProductSearch()
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productQuery, allProducts])

  const handleProductSearch = () => {
    const query = productQuery.trim().toLowerCase()
    if (!query) {
      setProductResults([])
      return
    }
    setIsSearchingProducts(true)
    try {
      const filtered = allProducts.filter((p) => {
        return (
          p.companyName?.toLowerCase().includes(query) ||
          p.modelNo?.toLowerCase().includes(query) ||
          p.invoiceNo?.toLowerCase().includes(query) ||
          p.size?.toLowerCase().includes(query) ||
          p.color?.toLowerCase().includes(query)
        )
      })
      setProductResults(filtered)
    } finally {
      setIsSearchingProducts(false)
    }
  }

  const handleDateSearch = async () => {
    if (!dateQuery) return
    setIsSearchingDate(true)
    setErrorMessage("")
    try {
      const res = await API.get(`/products/sales/by-date?date=${dateQuery}`)
      setDateResults(Array.isArray(res.data) ? res.data : [])
    } catch (err) {
      setDateResults([])
      setErrorMessage("Failed to search sales by date")
    } finally {
      setIsSearchingDate(false)
    }
  }

  const fetchProductDetails = async (productId) => {
    if (!productId) return
    setIsLoadingProduct(true)
    setErrorMessage("")
    try {
      const res = await API.get(`/products/${productId}/details`)
      setSelectedProduct(res.data)
      setSelectedProductId(productId)
    } catch (err) {
      setSelectedProduct(null)
      setErrorMessage(err.response?.data?.message || "Failed to load product details")
    } finally {
      setIsLoadingProduct(false)
    }
  }

  const onSelectProductFromList = (productId) => {
    fetchProductDetails(productId)
  }

  const onSelectFromDateResults = (sale) => {
    const productId = sale?.productId || sale?._id
    if (productId) fetchProductDetails(productId)
  }

  const clearSelection = () => {
    setSelectedProductId("")
    setSelectedProduct(null)
  }

  const formatDateSafe = (dateInput) => {
    if (!dateInput) return "Date N/A"
    try {
      const dateObj = dateInput instanceof Date ? dateInput : new Date(dateInput)
      if (isNaN(dateObj.getTime())) return "Date N/A"
      return format(dateObj, "dd MMM yyyy HH:mm")
    } catch (_) {
      return "Date N/A"
    }
  }

  const flattenedTransactions = useMemo(() => {
    if (!selectedProduct?.salesByCustomer) return []
    const rows = []
    selectedProduct.salesByCustomer.forEach((customer) => {
      customer.transactions?.forEach((t) => {
        rows.push({
          _id: t._id,
          date: t.date,
          quantity: t.quantity || 0,
          notes: t.notes || "",
          customerName: t.partyName || customer.customerName || "N/A",
        })
      })
    })
    return rows.sort((a, b) => new Date(b.date) - new Date(a.date))
  }, [selectedProduct])

  return (
    <div className="product-display-container">
      <div className="page-header">
        <h2>👁️ Product Display</h2>
      </div>

      <div className="search-panels">
        <div className="panel">
          <h3>🔎 Search by Product</h3>
          <div className="search-row">
            <input
              type="text"
              placeholder="Search by company, model, invoice, size or color..."
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
              className="text-input"
            />
            <button className="btn" onClick={handleProductSearch} disabled={isSearchingProducts}>
              {isSearchingProducts ? "Searching..." : "Search"}
            </button>
          </div>
          {productQuery && productResults.length > 0 && (
            <div className="list">
              {productResults.map((p) => (
                <div key={p._id} className="list-item" onClick={() => onSelectProductFromList(p._id)}>
                  <div className="list-main"><strong>{p.companyName}</strong> — {p.modelNo}</div>
                  <div className="list-sub">
                    {p.invoiceNo && <span>Invoice: {p.invoiceNo}</span>}
                    {p.size && <span>Size: {p.size}</span>}
                    {p.color && <span>Color: {p.color}</span>}
                    <span>Stock: {p.quantity}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {productQuery && productResults.length === 0 && !isSearchingProducts && (
            <div className="empty">No products found</div>
          )}
        </div>

        <div className="panel">
          <h3>📅 Search Sales by Date</h3>
          <div className="search-row">
            <input
              type="date"
              className="text-input"
              value={dateQuery}
              onChange={(e) => setDateQuery(e.target.value)}
            />
            <button className="btn" onClick={handleDateSearch} disabled={isSearchingDate || !dateQuery}>
              {isSearchingDate ? "Searching..." : "Search"}
            </button>
          </div>
          {dateResults.length > 0 && (
            <div className="table">
              <div className="table-head">
                <span>Product</span>
                <span>Model</span>
                <span>Qty</span>
                <span>Customer</span>
              </div>
              {dateResults.map((sale) => (
                <div key={sale._id} className="table-row clickable" onClick={() => onSelectFromDateResults(sale)}>
                  <span>{sale.companyName}</span>
                  <span>{sale.modelNo}</span>
                  <span className="qty">-{sale.quantity}</span>
                  <span>{sale.partyName || "N/A"}</span>
                </div>
              ))}
            </div>
          )}
          {dateQuery && dateResults.length === 0 && !isSearchingDate && (
            <div className="empty">No sales found for that date</div>
          )}
        </div>
      </div>

      {errorMessage && <div className="error">{errorMessage}</div>}

      <div className="details-section">
        {isLoadingProduct && <div className="loading">Loading product details...</div>}
        {!isLoadingProduct && selectedProduct && (
          <div className="details-card">
            <div className="details-header">
              <div>
                <h3>{selectedProduct.companyName} — {selectedProduct.modelNo}</h3>
                <p className="desc">{selectedProduct.description || "No description available"}</p>
              </div>
              <div className="header-actions">
                <button className="btn secondary" onClick={() => navigate(`/product-details/${selectedProductId}`)}>Open full page</button>
                <button className="btn" onClick={clearSelection}>Clear</button>
              </div>
            </div>

            <div className="stats">
              <div className="stat"><span className="label">Current Stock</span><span className="value">{selectedProduct.quantity}</span></div>
              <div className="stat"><span className="label">Total Sold</span><span className="value">{selectedProduct.totalSold}</span></div>
              <div className="stat"><span className="label">Customers</span><span className="value">{selectedProduct.uniqueCustomers}</span></div>
              <div className="stat"><span className="label">Size</span><span className="value">{selectedProduct.size || "N/A"}</span></div>
            </div>

            <h4>Sales History</h4>
            {flattenedTransactions.length === 0 ? (
              <div className="empty">No sales recorded for this product</div>
            ) : (
              <div className="table">
                <div className="table-head">
                  <span>Date</span>
                  <span>Customer</span>
                  <span>Qty</span>
                  <span>Notes</span>
                </div>
                {flattenedTransactions.map((t) => (
                  <div key={t._id} className="table-row">
                    <span>{formatDateSafe(t.date)}</span>
                    <span>{t.customerName}</span>
                    <span className="qty">-{t.quantity}</span>
                    <span>{t.notes || "-"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductDisplay


